import { Select } from '@/components/Select/Select';
import {
  COA_LINKS,
  COUNTRY_ACCS_TYPES,
  OSS_ACCS_TYPES,
  REVENUE_ACCS_TYPES,
  SHIPMENTS_ACCS_TYPES,
} from '@/components/ChartOfAccounts/ChartOfAccounts.helpers';
import PageLayout from '@/wrappers/PageLayout';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  activeCompanySelector,
  queryKeySelector,
  setActiveCompany,
  userSelector,
} from '@/core/store/user/userSlice';
import {
  APIEventsEnum,
  useAppDispatch,
  useAppSelector,
  useSendEvent,
} from '@/hooks';
import { AuthedRoutesEnum, ChartOfAccountsTypeEnum } from '@/core/enums';
import { useMemo, useState } from 'react';
import {
  Changelog,
  getChangelogs,
  getClientAccountNumbers,
  getCountries,
  postClientDatevDetails,
  postCOAClientNumber,
  postCOAConsultantNumber,
} from '@/core/api';
import { ClientDatevDetails } from '@/core/types';
import { COANumberModal } from '@/components/ChartOfAccounts/COANumberModal';
import { useModal } from '@/components/Modal/useModal';
import { COANumberBtn } from '@/components/ChartOfAccounts/COANumberBtn';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader } from '@/components/Loader/Loader';
import { COALastUpdated } from '@/components/ChartOfAccounts/COALastUpdated';
import { FisclalYearStartsModal } from '@/components/ChartOfAccounts/FisclalYearStartsModal';
import { normalizeDateBE, normalizeDateFE } from '@/core/normalization';
import { startOfMonth } from 'date-fns';

export default function ChartOfAccounts() {
  const { t } = useTranslation();
  const { sendEvent } = useSendEvent();
  const user = useAppSelector(userSelector);

  const activeCompany = useAppSelector(activeCompanySelector);
  const [isUpdatingCOAType, setIsUpdatingCOAType] = useState(false);
  const [isUpdatingAccountLength, setIsUpdatingAccountLength] = useState(false);
  const dispatch = useAppDispatch();
  const queryKey = useAppSelector(queryKeySelector);
  const queryClient = useQueryClient();

  const {
    data: clientAccountNumbers,
    isLoading: isLoadingClientAccountNumbers,
  } = useQuery({
    queryKey: ['clientAccountNumbers', queryKey],
    queryFn: () => getClientAccountNumbers(activeCompany?.company_id!),
    enabled: !!activeCompany?.company_id,
  });

  const {
    data: clientDatevDetailsChangelog,
    isLoading: isLoadingClientDatevDetailsChangelog,
  } = useQuery({
    queryKey: ['ClientDatevDetailsChangelog', queryKey],
    queryFn: () =>
      getChangelogs(activeCompany?.company_id || '', 'client_datev_details'),
    enabled: !!activeCompany?.company_id,
  });

  const {
    data: channelsAccsChangeLogs,
    isLoading: isLoadingChannelsAccsChangeLogs,
  } = useQuery({
    queryKey: ['ChannelsAccsChangeLogs', queryKey],
    queryFn: () =>
      getChangelogs(activeCompany?.company_id || '', 'channel_booking_code'),
    enabled: !!activeCompany?.company_id,
  });

  const revenueAccsIds = clientAccountNumbers
    ?.filter(
      (a) =>
        REVENUE_ACCS_TYPES.includes(a.account_type.account_type) &&
        (a.account_type.account_type === 'rest' ||
          a.country?.short_name === 'DE')
    )
    .map((a) => a.id);

  const {
    data: revenueAccsChangeLogs,
    isLoading: isLoadingRevenueAccsChangeLogs,
  } = useQuery({
    queryKey: ['RevenueAccsChangeLogs', queryKey],
    queryFn: () =>
      getChangelogs(
        activeCompany?.company_id || '',
        'account_number',
        revenueAccsIds
      ),
    enabled: !!activeCompany?.company_id && !!revenueAccsIds?.length,
  });

  const shipmentsAccsIds = clientAccountNumbers
    ?.filter(
      (a) =>
        a.account_type.account_type === 'rest' ||
        (SHIPMENTS_ACCS_TYPES.includes(a.account_type.account_type) &&
          (a.account_type.account_type === 'rest' ||
            a.country?.short_name === 'DE'))
    )
    .map((a) => a.id);

  const {
    data: shipmentsAccsChangeLogs,
    isLoading: isLoadingShipmentsAccsChangeLogs,
  } = useQuery({
    queryKey: ['ShipmentsAccsChangeLogs', queryKey],
    queryFn: () =>
      getChangelogs(
        activeCompany?.company_id || '',
        'account_number',
        shipmentsAccsIds
      ),
    enabled: !!activeCompany?.company_id && !!shipmentsAccsIds?.length,
  });

  const { data: countries, isLoading: isLoadingCountries } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountries,
  });

  const countriesList = useMemo(
    () =>
      countries?.filter(
        (c) =>
          (c.is_eu || c.short_name === 'SM') &&
          c.short_name !== 'XI' &&
          c.short_name !== 'MC' &&
          c.short_name !== 'DE'
      ),
    [countries]
  );

  const countriesAccsIds = clientAccountNumbers
    ?.filter((a) => {
      return (
        ((OSS_ACCS_TYPES.includes(a.account_type.account_type) && !a.country) ||
          (a.country &&
            countriesList?.some(
              (c) => c.short_name === a.country?.short_name
            ) &&
            COUNTRY_ACCS_TYPES.includes(a.account_type.account_type))) &&
        a.account_number
      );
    })
    .map((a) => a.id);

  const {
    data: countriesAccsChangeLogs,
    isLoading: isLoadingCountriesAccsChangeLogs,
  } = useQuery({
    queryKey: ['CountriesAccsChangeLogs', queryKey],
    queryFn: () =>
      getChangelogs(
        activeCompany?.company_id || '',
        'account_number',
        countriesAccsIds
      ),
    enabled: !!activeCompany?.company_id && !!countriesAccsIds?.length,
  });

  const [COAModalData, setCOAModalData] = useState<{
    title: string;
    label: string;
    initialValue: string;
    field: keyof ClientDatevDetails;
    apiFn: (
      companyId: string,
      val: number | null
    ) => Promise<ClientDatevDetails>;
  }>();

  const {
    isModalOpened: isCOANumberModalOpened,
    closeModal: closeCOANumberModal,
    openModal: openCOANumberModal,
  } = useModal();

  const {
    isModalOpened: isFiscalYearStartsModalOpened,
    closeModal: closeFiscalYearStartsModal,
    openModal: openFiscalYearStartsModal,
  } = useModal();

  const onChange = (
    v: string | number,
    field: keyof ClientDatevDetails,
    setIsLoading: (v: boolean) => void
  ) => {
    if (!activeCompany?.ClientDatevDetails) return;
    setIsLoading(true);
    postClientDatevDetails(activeCompany!.company_id, {
      [field]: v,
    })
      .then((res) => {
        dispatch(
          setActiveCompany({
            ...activeCompany,
            ClientDatevDetails: res,
          })
        );

        return getChangelogs(
          activeCompany?.company_id || '',
          'client_datev_details'
        );
      })
      .then((res) => {
        queryClient.setQueryData(
          ['ClientDatevDetailsChangelog', queryKey],
          res
        );
      })
      .catch(console.error)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderFiscalYearStarts = () => {
    if (!activeCompany?.ClientDatevDetails?.fiscal_year_start) return undefined;
    const date = normalizeDateFE(
      activeCompany?.ClientDatevDetails?.fiscal_year_start
    );
    const monthStr =
      date.getMonth() + 1 < 10
        ? `0${date.getMonth() + 1}`
        : date.getMonth() + 1;
    return `01.${monthStr}.`;
  };

  if (
    isLoadingClientDatevDetailsChangelog ||
    isLoadingChannelsAccsChangeLogs ||
    isLoadingRevenueAccsChangeLogs ||
    isLoadingShipmentsAccsChangeLogs ||
    isLoadingClientAccountNumbers ||
    isLoadingCountriesAccsChangeLogs ||
    isLoadingCountries
  ) {
    return <Loader />;
  }

  return (
    <PageLayout className='flex-grow' rightColumn={<div />}>
      <div
        className='max-w-[712px] rounded-lg bg-white px-6 pb-6 pt-4'
        data-testid='seller-chart-of-accounts'
      >
        <div className='text-h3 font-black'>{t('Chart of accounts')}</div>
        {!!clientDatevDetailsChangelog?.length && (
          <div className='text-sm text-gray-700'>
            {t('Last updated')}:{' '}
            <COALastUpdated
              date={clientDatevDetailsChangelog[0].created_at}
              role={clientDatevDetailsChangelog[0].role_name}
            />
          </div>
        )}
        <div className='space-y-2 py-4'>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='font-semibold'>
              {t('DATEV standard chart of accounts')}
            </div>
            <div>
              <Select
                name='SKR'
                isLoading={isUpdatingCOAType}
                disabled={
                  isUpdatingCOAType ||
                  !activeCompany?.ClientDatevDetails?.chat_of_account_type
                }
                options={[
                  {
                    id: ChartOfAccountsTypeEnum.SKR03,
                    name: ChartOfAccountsTypeEnum.SKR03,
                  },
                  {
                    id: ChartOfAccountsTypeEnum.SKR04,
                    name: ChartOfAccountsTypeEnum.SKR04,
                  },
                ]}
                value={activeCompany?.ClientDatevDetails?.chat_of_account_type}
                onChange={(v) => {
                  onChange(v, 'chat_of_account_type', setIsUpdatingCOAType);
                }}
                className='Select--COA !min-w-[144px] !pb-0'
                triggerClassname='!bg-white !py-1 !pl-3 !pr-2'
                arrowClassname='h-6 w-6'
              />
            </div>
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='font-semibold'>
              {t('Length of general ledger accounts')}
            </div>
            <div>
              <Select
                placeholder='Select'
                name='accounts_length'
                isLoading={isUpdatingAccountLength}
                disabled={isUpdatingAccountLength}
                options={[
                  {
                    id: 4,
                    name: '4',
                  },
                  {
                    id: 5,
                    name: '5',
                  },
                  {
                    id: 6,
                    name: '6',
                  },
                  {
                    id: 7,
                    name: '7',
                  },
                ]}
                value={activeCompany?.ClientDatevDetails?.accounts_length}
                onChange={(v) => {
                  onChange(v, 'accounts_length', setIsUpdatingAccountLength);
                }}
                className='Select--COA !min-w-[144px] !pb-0'
                triggerClassname='!bg-white !py-1 !pl-3 !pr-2'
                arrowClassname='h-6 w-6'
              />
            </div>
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='font-semibold'>
              {t('Debtor and vendor accounts')}
            </div>
            <div className='font-semibold text-gray-700'>
              {activeCompany?.ClientDatevDetails?.accounts_length_credit && (
                <>
                  {activeCompany.ClientDatevDetails.accounts_length_credit}
                  &nbsp;{t('e.g.')} {'8320'}
                  {activeCompany.ClientDatevDetails.accounts_length_credit > 4
                    ? Array(
                        activeCompany.ClientDatevDetails
                          .accounts_length_credit - 4
                      )
                        .fill('0')
                        .join('')
                    : ''}
                </>
              )}
            </div>
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='font-semibold'>{t('Consultant number')}</div>
            <COANumberBtn
              value={activeCompany?.ClientDatevDetails?.consultant_number}
              onClick={() => {
                setCOAModalData({
                  title: 'Add the DATEV consultant number',
                  label: 'Consultant number',
                  initialValue: String(
                    activeCompany?.ClientDatevDetails?.consultant_number || ''
                  ),
                  field: 'consultant_number',
                  apiFn: postCOAConsultantNumber,
                });
                openCOANumberModal();
              }}
              testId='consultant_number_btn'
            />
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='font-semibold'>{t('Client number')}</div>
            <COANumberBtn
              value={activeCompany?.ClientDatevDetails?.client_number}
              onClick={() => {
                setCOAModalData({
                  title: 'Add the DATEV client number',
                  label: 'Client number',
                  initialValue: String(
                    activeCompany?.ClientDatevDetails?.client_number || ''
                  ),
                  field: 'client_number',
                  apiFn: postCOAClientNumber,
                });
                openCOANumberModal();
              }}
              testId='client_number_btn'
            />
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='font-semibold'>{t('Fiscal year starts on')}</div>
            <COANumberBtn
              value={renderFiscalYearStarts()}
              onClick={() => {
                openFiscalYearStartsModal();
              }}
              testId='fiscal_year_starts_btn'
            />
          </div>
        </div>
        <div className='border-t pb-4 pt-2'>
          <div className='font-bold'>{t('Accounts')}</div>
          <div className='text-gray-700'>
            {t(
              'All DATEV files created from this date onwards will be created with the following configuration. If you want older reports to use this configuration, please create the files again.'
            )}
          </div>
        </div>
        <div className='divide-y rounded-lg border'>
          {COA_LINKS.map((v) => {
            let changelogs: Changelog[] = [];
            if (v.to === AuthedRoutesEnum.COADebtor) {
              changelogs = channelsAccsChangeLogs || [];
            }
            if (v.to === AuthedRoutesEnum.COARevenue) {
              changelogs = revenueAccsChangeLogs || [];
            }
            if (v.to === AuthedRoutesEnum.COAShipments) {
              changelogs = shipmentsAccsChangeLogs || [];
            }
            if (v.to === AuthedRoutesEnum.COACountries) {
              changelogs = countriesAccsChangeLogs || [];
            }

            const changelog = changelogs?.[0];

            return (
              <div
                className='flex items-center justify-between gap-x-4 px-4 py-3'
                key={v.label}
              >
                <div className='font-semibold'>{t(v.label)}</div>
                <div className='flex items-center gap-x-2'>
                  {!!changelog && (
                    <COALastUpdated
                      testId={`last_updated-${v.label}`}
                      date={changelog.created_at}
                      role={changelog.role_name}
                    />
                  )}
                  <Link
                    className='Button--outline Button--sm block min-w-[104px] text-center'
                    to={v.to}
                    onClick={() => {
                      sendEvent(APIEventsEnum.click_chart_of_accounts_link, {
                        link: v.label,
                        role: user?.Role || '',
                      });
                    }}
                  >
                    {t('Open')}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <COANumberModal
        isModalOpened={isCOANumberModalOpened}
        closeModal={closeCOANumberModal}
        title={COAModalData?.title || ''}
        label={COAModalData?.label || ''}
        initialValue={COAModalData?.initialValue || ''}
        onSubmit={async (v: string | null) => {
          if (
            !activeCompany?.company_id ||
            !COAModalData?.field ||
            !COAModalData?.apiFn
          )
            return;

          try {
            const res = await COAModalData.apiFn(
              activeCompany!.company_id,
              v ? Number(v) : null
            );

            dispatch(
              setActiveCompany({
                ...activeCompany,
                ClientDatevDetails: {
                  ...(activeCompany.ClientDatevDetails || {}),
                  [COAModalData.field]: res[COAModalData.field],
                },
              })
            );

            closeCOANumberModal();
          } catch (error) {
            console.error(error);
          }
        }}
      />
      <FisclalYearStartsModal
        isModalOpened={isFiscalYearStartsModalOpened}
        closeModal={closeFiscalYearStartsModal}
        value={
          activeCompany?.ClientDatevDetails?.fiscal_year_start
            ? normalizeDateFE(
                activeCompany?.ClientDatevDetails?.fiscal_year_start
              ).getMonth()
            : undefined
        }
        onSubmit={async (v: number) => {
          if (!activeCompany?.company_id) return;

          const date = new Date();
          date.setMonth(v);

          try {
            const res = await postClientDatevDetails(
              activeCompany!.company_id,
              {
                fiscal_year_start: normalizeDateBE(startOfMonth(date)),
              }
            );

            dispatch(
              setActiveCompany({
                ...activeCompany,
                ClientDatevDetails: res,
              })
            );
            closeFiscalYearStartsModal();
          } catch (error) {
            console.error(error);
          }
        }}
      />
    </PageLayout>
  );
}
